
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export interface Option {
  value: string;
  label: string;
}

interface MultiSearchableSelectProps {
  label: string;
  name: string;
  value: string[];
  options: Option[];
  onChange: (name: string, value: string[], labels: string[]) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  loading?: boolean;
}

const MultiSearchableSelect: React.FC<MultiSearchableSelectProps> = ({
  label,
  name,
  value = [],
  options = [],
  onChange,
  placeholder = "Select",
  required = false,
  disabled = false,
  error,
  loading = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
    openUpward: boolean;
  } | null>(null);

  const selectedValues = Array.isArray(value) ? value : [];

  const validOptions = Array.isArray(options) ? options : [];

  const selectedOptions = selectedValues.map((selectedValue) => {
    return (
      validOptions.find((option) => option.value === selectedValue) ?? {
        value: selectedValue,
        label: selectedValue,
      }
    );
  });

  const filteredOptions = validOptions.filter((option) =>
    option.label.toLowerCase().includes(search.toLowerCase()),
  );

  const allSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((option) =>
      selectedValues.includes(option.value),
    );

  const calculatePosition = () => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUpward = spaceBelow < 260 && spaceAbove > spaceBelow;

    setDropdownPos({
      top: openUpward ? rect.top : rect.bottom,
      left: rect.left,
      width: rect.width,
      openUpward,
    });
  };

  const toggleOpen = () => {
    if (disabled) return;

    if (!isOpen) calculatePosition();

    setIsOpen((previous) => !previous);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (
        !containerRef.current?.contains(target) &&
        !target.closest("[data-multi-searchable-dropdown]")
      ) {
        setIsOpen(false);
        setSearch("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const reposition = () => calculatePosition();

    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);

    return () => {
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen]);

  const updateSelection = (newValues: string[]) => {
    const uniqueValues = Array.from(new Set(newValues));

    const labels = uniqueValues.map((selectedValue) => {
      return (
        validOptions.find((option) => option.value === selectedValue)
          ?.label ?? selectedValue
      );
    });

    onChange(name, uniqueValues, labels);
  };

  const handleOptionChange = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((item) => item !== optionValue)
      : [...selectedValues, optionValue];

    updateSelection(newValues);
  };

  const handleSelectAll = () => {
    const filteredValues = filteredOptions.map((option) => option.value);

    if (allSelected) {
      updateSelection(
        selectedValues.filter((item) => !filteredValues.includes(item)),
      );
    } else {
      updateSelection([...selectedValues, ...filteredValues]);
    }
  };

  return (
    <div ref={containerRef} className="position-relative w-100">
      <label className="form-label">
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>

      <div
        ref={triggerRef}
        className="form-select d-flex justify-content-between align-items-center"
        style={{
          minHeight: "38px",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: disabled ? "#f5f5f5" : "#fff",
        }}
        onClick={toggleOpen}
      >
        <span
          className={selectedValues.length ? "" : "text-muted"}
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {selectedValues.length
            ? `${selectedValues.length} selected`
            : placeholder}
        </span>
      </div>

      {error && (
        <div className="text-danger small mt-1">{error}</div>
      )}

     {selectedOptions.length > 0 && (
  <div
    className="mt-1 d-flex flex-wrap gap-1"
    style={{
      fontSize: "12px",
      lineHeight: "17px",
      color: "#54729a",
      maxHeight: "65px",
      overflowY: "auto",
      overflowX: "hidden",
    }}
  >
    {selectedOptions.map((option) => (
      <span
        key={option.value}
        style={{
          background: "#f1f5f9",
          border: "1px solid #dbe3ec",
          borderRadius: "4px",
          padding: "2px 6px",
          whiteSpace: "nowrap",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={option.label}
      >
        {option.label}
      </span>
    ))}
  </div>
)}

      {isOpen &&
        dropdownPos &&
        createPortal(
          <div
            data-multi-searchable-dropdown
            className="border rounded shadow-sm bg-white position-fixed"
            style={{
              top: dropdownPos.openUpward
                ? undefined
                : dropdownPos.top + 4,
              bottom: dropdownPos.openUpward
                ? window.innerHeight - dropdownPos.top + 4
                : undefined,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 2000,
            }}
          >
            <div className="p-2 border-bottom">
              <input
                type="text"
                className="form-control"
                placeholder="Search..."
                value={search}
                autoFocus
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <div
              className="px-3 py-2 border-bottom"
              style={{ cursor: "pointer" }}
              onClick={handleSelectAll}
            >
              <div className="form-check mb-0">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={allSelected}
                  onChange={handleSelectAll}
                  onClick={(event) => event.stopPropagation()}
                />
                <label className="form-check-label">
                  Select All
                </label>
              </div>
            </div>

            <div style={{ maxHeight: "200px", overflowY: "auto" }}>
              {loading ? (
                <div className="px-3 py-2 text-muted">Loading...</div>
              ) : filteredOptions.length ? (
                filteredOptions.map((option) => {
                  const checked = selectedValues.includes(option.value);

                  return (
                    <div
                      key={option.value}
                      className="px-3 py-2"
                      style={{
                        cursor: "pointer",
                        backgroundColor: checked ? "#fff3eb" : "#fff",
                      }}
                      onClick={() => handleOptionChange(option.value)}
                    >
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={checked}
                          readOnly
                        />
                        <label className="form-check-label text-capitalize">
                          {option.label}
                        </label>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-muted">
                  No results found
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default MultiSearchableSelect;
